import { useState, useEffect, useCallback } from 'react';
import apiClient from '@/lib/api/client';
import { MainLayout } from '@/components/layout/MainLayout';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MonthActual {
  month: string;
  month_name: string;
  billed: number;
  collected: number;
  invoice_count: number;
}

interface MonthProjected extends MonthActual {
  projected: number;
}

interface PipelineJob {
  id: number;
  title: string;
  client_name: string;
  status: string;
  budget: number;
  invoiced: number;
  uninvoiced: number;
}

interface ProjectionData {
  year: number;
  months_elapsed: number;
  months_remaining: number;
  actuals: MonthActual[];
  projected: MonthProjected[];
  summary: {
    actuals_total: number;
    run_rate_monthly: number;
    projected_remainder: number;
    pipeline_total: number;
    projected_annual: number;
  };
  pipeline_jobs: PipelineJob[];
  seasonal_weights: number[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const money = (n: number) =>
  `$${Math.round(n).toLocaleString('en-CA')}`;

const moneyFull = (n: number) =>
  `$${(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const STATUS_LABEL: Record<string, string> = {
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  draft: 'Draft',
};

// ─── Main component ───────────────────────────────────────────────────────────

export const ProjectionsPage = () => {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [data, setData] = useState<ProjectionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [weights, setWeights] = useState<number[]>([0.70, 0.75, 0.85, 1.00, 1.10, 1.20, 1.20, 1.15, 1.10, 1.00, 0.85, 0.70]);
  const [includePipeline, setIncludePipeline] = useState(true);
  const [showWeightEditor, setShowWeightEditor] = useState(false);

  const yearOptions = [thisYear - 1, thisYear, thisYear + 1];

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await apiClient.get('/projections', {
        params: {
          year,
          weights: JSON.stringify(weights),
          includePipeline: String(includePipeline),
        },
      });
      setData(res.data);
      setWeights(res.data.seasonal_weights);
    } catch {
      setError('Failed to load projection data.');
    } finally {
      setLoading(false);
    }
  }, [year, includePipeline]);

  useEffect(() => { load(); }, [load]);

  const applyWeights = () => {
    setShowWeightEditor(false);
    load();
  };

  // Build unified chart months: 12 months total
  const chartMonths = data
    ? data.actuals.map((a, i) => ({
        name: MONTH_ABBR[i],
        value: i < data.months_elapsed ? a.billed : (data.projected[i - data.months_elapsed]?.projected ?? 0),
        isProjected: i >= data.months_elapsed,
      }))
    : [];

  const chartMax = chartMonths.length > 0 ? Math.max(...chartMonths.map(m => m.value), 1) : 1;

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Revenue Projection</h1>
            <p className="text-sm text-gray-500 mt-0.5">Annual forecast based on actuals-to-date with seasonal adjustment</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={year}
              onChange={e => setYear(Number(e.target.value))}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
        )}

        {loading ? (
          <div className="py-20 text-center">
            <span className="material-symbols-outlined animate-spin text-3xl text-gray-300 block mx-auto mb-2">progress_activity</span>
            <p className="text-sm text-gray-400">Calculating projection…</p>
          </div>
        ) : data ? (
          <>
            {/* ── Hero KPI ──────────────────────────────────────────────────── */}
            <div className="bg-gradient-to-br from-[#C77D3F] to-[#8B5A2B] rounded-2xl p-6 text-white shadow-md">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-white/70 text-sm font-medium mb-1">Projected {year} Revenue</p>
                  <p className="text-4xl font-bold tracking-tight">{money(data.summary.projected_annual)}</p>
                  <p className="text-white/60 text-xs mt-2">
                    Based on {data.months_elapsed} month{data.months_elapsed !== 1 ? 's' : ''} of actuals
                    {' '}· {money(data.summary.run_rate_monthly)}/mo run rate
                    {' '}· seasonally adjusted
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-sm text-white/70 mb-1">Actuals to date</div>
                  <div className="text-xl font-semibold">{money(data.summary.actuals_total)}</div>
                  {data.months_remaining > 0 && (
                    <>
                      <div className="text-sm text-white/70 mt-2 mb-1">Projected remainder</div>
                      <div className="text-xl font-semibold">{money(data.summary.projected_remainder)}</div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* ── Monthly bar chart ─────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-gray-900">Monthly Breakdown</h2>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-sm bg-[#C77D3F]" />
                    Actual
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-sm bg-[#C77D3F]/30 border border-[#C77D3F]/40" />
                    Projected
                  </span>
                </div>
              </div>
              <div className="flex items-end gap-1.5 h-40">
                {chartMonths.map((m, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex flex-col justify-end" style={{ height: '128px' }}>
                      <div
                        className={`w-full rounded-t transition-all ${m.isProjected ? 'bg-[#C77D3F]/30 border border-[#C77D3F]/40' : 'bg-[#C77D3F]'}`}
                        style={{ height: `${Math.max(2, (m.value / chartMax) * 128)}px` }}
                        title={`${m.name}: ${money(m.value)}`}
                      />
                    </div>
                    <span className={`text-[10px] font-medium ${m.isProjected ? 'text-gray-400' : 'text-gray-600'}`}>{m.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Build-up table ────────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900">Projection Build-Up</h2>
              </div>
              <div className="divide-y divide-gray-50">
                <div className="flex items-center justify-between px-5 py-3.5">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Actuals to date ({data.months_elapsed} months)</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {data.actuals.slice(0, data.months_elapsed).filter(m => m.billed > 0).map(m => m.month_name).join(', ') || 'No invoices yet'}
                    </p>
                  </div>
                  <p className="font-semibold text-gray-900">{money(data.summary.actuals_total)}</p>
                </div>

                {data.months_remaining > 0 && (
                  <div className="flex items-center justify-between px-5 py-3.5">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Seasonal projection ({data.months_remaining} months)</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {money(data.summary.run_rate_monthly)}/mo run rate × seasonal weights
                      </p>
                    </div>
                    <p className="font-semibold text-gray-900">+ {money(data.summary.projected_remainder)}</p>
                  </div>
                )}

                <div className="flex items-center justify-between px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Booked pipeline</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {data.pipeline_jobs.length} job{data.pipeline_jobs.length !== 1 ? 's' : ''} not yet fully invoiced
                      </p>
                    </div>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includePipeline}
                        onChange={e => setIncludePipeline(e.target.checked)}
                        className="rounded border-gray-300 text-primary focus:ring-primary/30 h-3.5 w-3.5"
                      />
                      <span className="text-xs text-gray-500">Include</span>
                    </label>
                  </div>
                  <p className={`font-semibold ${includePipeline ? 'text-gray-900' : 'text-gray-300'}`}>
                    + {money(data.summary.pipeline_total)}
                  </p>
                </div>

                <div className="flex items-center justify-between px-5 py-4 bg-gray-50">
                  <p className="font-bold text-gray-900">Projected {year} Annual</p>
                  <p className="text-xl font-bold text-[#C77D3F]">{money(data.summary.projected_annual)}</p>
                </div>
              </div>
            </div>

            {/* ── Pipeline jobs ─────────────────────────────────────────────── */}
            {data.pipeline_jobs.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100">
                  <h2 className="font-semibold text-gray-900">Pipeline Jobs</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Scheduled/in-progress jobs with remaining budget not yet invoiced</p>
                </div>
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide">
                      <th className="text-left px-5 py-3 font-semibold">Job</th>
                      <th className="text-left px-5 py-3 font-semibold hidden sm:table-cell">Client</th>
                      <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">Status</th>
                      <th className="text-right px-5 py-3 font-semibold">Budget</th>
                      <th className="text-right px-5 py-3 font-semibold">Invoiced</th>
                      <th className="text-right px-5 py-3 font-semibold">Remaining</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data.pipeline_jobs.map(job => (
                      <tr key={job.id} className="hover:bg-gray-50">
                        <td className="px-5 py-3 font-medium text-gray-900">{job.title}</td>
                        <td className="px-5 py-3 text-gray-600 hidden sm:table-cell">{job.client_name}</td>
                        <td className="px-5 py-3 hidden md:table-cell">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            {STATUS_LABEL[job.status] || job.status}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right text-gray-700">{moneyFull(job.budget)}</td>
                        <td className="px-5 py-3 text-right text-gray-600">{moneyFull(job.invoiced)}</td>
                        <td className="px-5 py-3 text-right font-semibold text-[#C77D3F]">{moneyFull(job.uninvoiced)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}

            {/* ── Seasonal weights editor ───────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <button
                onClick={() => setShowWeightEditor(v => !v)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="text-left">
                  <h2 className="font-semibold text-gray-900">Seasonal Weights</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Adjust per-month multipliers to reflect your business pattern</p>
                </div>
                <span className="material-symbols-outlined text-gray-400">
                  {showWeightEditor ? 'expand_less' : 'expand_more'}
                </span>
              </button>
              {showWeightEditor && (
                <div className="px-5 pb-5 border-t border-gray-100">
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 mt-4">
                    {MONTH_ABBR.map((name, i) => (
                      <div key={i} className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-600">{name}</label>
                        <input
                          type="number"
                          step="0.05"
                          min="0"
                          max="3"
                          value={weights[i]}
                          onChange={e => {
                            const v = parseFloat(e.target.value);
                            if (!isNaN(v)) {
                              setWeights(prev => prev.map((w, idx) => idx === i ? v : w));
                            }
                          }}
                          className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-4 justify-end">
                    <button
                      onClick={() => setWeights([0.70, 0.75, 0.85, 1.00, 1.10, 1.20, 1.20, 1.15, 1.10, 1.00, 0.85, 0.70])}
                      className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
                    >
                      Reset to defaults
                    </button>
                    <button
                      onClick={applyWeights}
                      className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* ── Methodology footnotes ─────────────────────────────────────── */}
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
              <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Methodology Notes</h3>
              <ul className="space-y-1.5 text-xs text-gray-600">
                <li>• <strong>Actuals</strong>: Sum of invoice subtotals (pre-tax) for {year}, excluding draft, cancelled, and revised invoices.</li>
                <li>• <strong>Run rate</strong>: Actuals ÷ months elapsed ({data.months_elapsed} months). Projects a flat continuation of recent performance.</li>
                <li>• <strong>Seasonal adjustment</strong>: Remaining months are weighted by the multipliers above, then scaled so the total projected months sum to the appropriate share of the year.</li>
                <li>• <strong>Pipeline</strong>: Job budget minus amount already invoiced, for jobs in Scheduled or In Progress status. Does not include reimbursable expenses.</li>
                <li>• <strong>Projected annual</strong>: Actuals + seasonally-adjusted remainder {includePipeline ? '+ pipeline' : '(pipeline excluded)'}. This figure reflects labour and materials billed; consult your accountant for insurance or licensing submissions.</li>
              </ul>
            </div>
          </>
        ) : null}
      </div>
    </MainLayout>
  );
};
